import { Alignment, Api, CurrencySettings, FieldUpdateEvent, ModelRow, StringUtil } from "@mcleod/core";
import { Component, ComponentCreator, Cursor, DataSourceAction, DataSourceExecutionEvent, Image, Label, Panel, Textbox } from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { AssetUtil } from "@mcleod/general";
import { AutogenLayoutOrderOverview } from "./autogen/AutogenLayoutOrderOverview";
import { DistanceAlertUtil } from "./DistanceAlertUtil";

export class OrderOverview extends AutogenLayoutOrderOverview {
    private distanceAlertUtil = new DistanceAlertUtil();

    override onLoad() {
        this.textboxContactName._captionLabel.color = "primary.reverse";
        this.textboxSalespersonId._captionLabel.color = "primary.reverse";
        this.textboxName._captionLabel.color = "primary.reverse";
        this.panelDataFusion.visible = false;
        this.mainDataSource.displayDataInBoundComponents();
    }

    async sourceOrdersAfterExecution(event) {
        const row = this.mainDataSource.activeRow;
        this.textboxCommodity.text = row?.getFirstLookupModelData("commodity_id")?.get("descr");
        this.addReferenceNumberTextboxes();
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setCarrierRateData();
            const customerId = row?.get("customer_id");
            if (customerId != null){
               await this.sourceCustomer.search({ "id": customerId });
            }

            if (row) {
                row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => this.orderFieldUpdated(event))
            }

            const status = row?.get("status");
            this.panelDelivered.visible = "D" == status;
            this.panelCommodity.visible = "D" != status;
        }

        const isAsset = AssetUtil.isAssetMoveType(row);
        this.textboxTotalPay.visible = !isAsset;
        this.textboxMargin.visible = !isAsset;
    }

    orderFieldUpdated(event: FieldUpdateEvent) {
        if ("customer_id" == event.fieldName && StringUtil.isEmptyString(event.newValue))
            this.customerChanged(null);
        else if ("commodity_id" == event.fieldName)
            this.textboxCommodity.text = event.originator?.text || "--";
    }

    textboxNameOnDataDisplay() {
        CommonTooltips.setTooltipContentLayout(this.textboxName, this.activeRow?.get("customer_id"), "lme/ar/CustomerQuickInfo");
    }

    customerChanged(customerData: ModelRow<any>) {
        this.sourceCustomer.data = [];
        if (customerData != null) {
            this.sourceCustomer.data = [customerData];
            this.sourceCustomer.setRowIndexWithoutUpdate(0);
        }
        this.sourceCustomer.displayDataInBoundComponents();
    }

    private addReferenceNumberTextboxes(referenceNumbers?: ModelRow<any>[]) {
        this.panelRefNbrs.removeAll();
        const row = this.sourceOrders.activeRow;
        this.addReferenceNumberTextbox("BOL", row.get("blnum"), "blnum");
        this.addReferenceNumberTextbox("Consignee Reference Number", row.get("consignee_refno"), "consignee_refno");
    }

    addReferenceNumberTextbox = function (caption: string, text: string, field?: string) {
        if (caption != null && text != null) {
            const textbox = new Textbox({ marginBottom: 3, caption: caption, text: text, printable: true, fontSize: "large", fontBold: true, fillRow: true });
            textbox._captionLabel.fontSize = "medium";
            if (field != null) {
                textbox.field = field;
                textbox.dataSource = this.mainDataSource;
            }
            this.panelRefNbrs.add(textbox);
        }
    }

    setCarrierRateData() {
        const moveId = this.mainDataSource.activeRow.get("movement.id");
        const orderFreightCharge = this.mainDataSource.activeRow?.getCurrency("freight_charge");
        const orderCurrencyCode = orderFreightCharge?.getCurrencyCode();
        const orderConversionDate = orderFreightCharge?.getConversionDate();
        let moveCurrencyCode: string = null;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const moveCarrierPay = this.mainDataSource.activeRow?.getCurrency("movement.override_pay_amt");
            moveCurrencyCode = moveCarrierPay != null ? moveCarrierPay.getCurrencyCode() : orderCurrencyCode;
        }
        if (moveId != null) {
            this.sourceCalculateOverridePay.search({
                "movement_id": moveId,
                "order_id": this.mainDataSource.activeRow?.get("id"),
                "calculate_order_total": true,
                "move_currency_code": moveCurrencyCode,
                "conversion_date": orderConversionDate
            });
        }
    }

    /** This is an event handler for the afterExecution event of sourceDistanceAlert. */
    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        if (this.distanceAlertUtil.unclearedDistanceAlertsExist(this.sourceDistanceAlert)) {
            const row = this.sourceDistanceAlert.data[this.sourceDistanceAlert.data.length - 1];
            this.billingDistanceDangerImage.visible = true;
            for (const component of this.panelBillingDistance.components) {
                const label = component as Label;
                if (!StringUtil.isEmptyString(label.field)) {
                    label.color = "warning";
                    this.billingDistanceDangerImage.color = "warning";
                    this.labelBillingDistance.text = "0.0";
                }
                this.createLabelHoverToDiscover(label, row);
                label.allowTooltip = true;
            }
        } else {
            this.setDistanceWarningComponentsToDefault(this.billingDistanceDangerImage, this.panelBillingDistance);
        }
    }

    async sourceOrderOnDisplay() {
        const movementId = this.mainDataSource.activeRow?.get("movement.id") as string;
        const orderId = this.mainDataSource.activeRow?.get("id") as string;
        const customerId = this.mainDataSource.activeRow?.get("customer_id") as string;
        const companyId = this.mainDataSource.activeRow?.get("company_id") as string;
        const pbLtl = this.mainDataSource.activeRow?.get("is_brokerage_ltl");
        const hasIntercompany = this.mainDataSource.activeRow?.get("has_intercompany") as boolean;
        const hasICerror = this.mainDataSource.activeRow?.get("has_ic_error") as boolean;

        const layoutOrderDataFusionSummary = this.layoutOrderDataFusionSummary;
        if (movementId && orderId && customerId) {
            layoutOrderDataFusionSummary.movementId = movementId;
            layoutOrderDataFusionSummary.orderId = orderId;
            layoutOrderDataFusionSummary.customerId = customerId;
            layoutOrderDataFusionSummary.companyId = companyId;
            layoutOrderDataFusionSummary.hasIntercompany = hasIntercompany;
            layoutOrderDataFusionSummary.hasICError = hasICerror;

            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId,
                    is_brokerage_ltl: pbLtl
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/order-datafusion-summary", body).then(response => {
                if (response) {
                    layoutOrderDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutOrderDataFusionSummary.displayingData;
                }
            });
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private setDistanceWarningComponentsToDefault(distanceWarningImage: Image, panelDistance: Panel) {
        distanceWarningImage.visible = false;
        for (const component of panelDistance.components) {
            const label = component as Label;
            if (!StringUtil.isEmptyString(label.field)) {
                label.color = "default";
            }
        }
        const labelMI = this.panelBillingDistance.findComponentById("labelMI") as Label;
        if (labelMI) {
            labelMI.color = "default";
        }
        this.suppressLabelTooltip(panelDistance);
    }

    private createLabelHoverToDiscover(label: Label, row: any) {
        label.tooltipPosition = Alignment.RIGHT;
        const panel = new Panel({ id: "panelHoverToDisplay", maxWidth: 400, cursor: Cursor.DEFAULT }) as Panel;
        const labelOne = new Label({ id: "hoverIdOne", fontBold: true, fontSize: "xlarge", text: `Distance lookup warning`, rowBreak: false });
        const labelTwo = new Label({ id: "hoverIdTwo", text: "" + row?.get("comments"), rowBreak: true });
        panel.add(labelOne);
        panel.add(labelTwo);

        label.tooltipDynamicContent = panel;
    }

    private suppressLabelTooltip(panelDistance: Panel) {
        for (const component of panelDistance.components) {
            const label = component as Label;
            label.allowTooltip = false;
        }
    }
}
